import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_QUOTATION, UPDATE_QUOTATION_STATUS } from '../../apollo/queries';
import { uploadFileToS3, validateFile } from '../../utils/s3Upload';
import '../css/QuotationResponsePage.css';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';

const QuotationResponsePage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const quoteId = searchParams.get('id');
    
    // 상태 관리
    const [selectedItems, setSelectedItems] = useState([]);
    const [adminMemo, setAdminMemo] = useState('');
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [includeVat, setIncludeVat] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFileUrl, setUploadedFileUrl] = useState('');
    const [fileName, setFileName] = useState('선택된 파일 없음');

    // GraphQL 쿼리
    const { data, loading, error, refetch } = useQuery(GET_QUOTATION, {
        variables: { id: parseInt(quoteId) },
        skip: !quoteId,
        errorPolicy: 'all',
        onCompleted: (data) => {
            // 기존 관리자 답변이 있으면 메모에 설정
            if (data?.quotation?.adminReply) {
                setAdminMemo(data.quotation.adminReply);
            }
            // 기존 첨부파일 URL이 있으면 설정
            if (data?.quotation?.attachmentUrl) {
                setUploadedFileUrl(data.quotation.attachmentUrl);
                setFileName('기존 첨부파일');
            }
        }
    });

    // 견적 상태 업데이트 뮤테이션
    const [updateQuotationStatus, { loading: updating }] = useMutation(UPDATE_QUOTATION_STATUS, {
        onCompleted: (data) => {
            alert('견적 답변이 완료되었습니다.');
            navigate('/admin/quotes/pending');
        },
        onError: (error) => {
            console.error('견적 답변 에러:', error);
            alert('견적 답변 중 오류가 발생했습니다.');
        }
    });

    // 이벤트 핸들러들
    const handleItemCheck = (itemId, checked) => {
        if (checked) {
            setSelectedItems(prev => [...prev, itemId]);
        } else {
            setSelectedItems(prev => prev.filter(id => id !== itemId));
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            // PDF 파일 유효성 검사
            validateFile(file, 50 * 1024 * 1024, ['application/pdf']); // 10MB 제한, PDF만
            
        setAttachmentFile(file);
            setFileName(file.name);
            
            // S3 업로드
            setIsUploading(true);
            const uploadedUrl = await uploadFileToS3(file, 'quotation-responses');
            setUploadedFileUrl(uploadedUrl);
            
            console.log('PDF 파일 업로드 완료:', uploadedUrl);
            
        } catch (error) {
            alert(`파일 업로드 오류: ${error.message}`);
            setAttachmentFile(null);
            setFileName('선택된 파일 없음');
            setUploadedFileUrl('');
        } finally {
            setIsUploading(false);
        }
    };

    // 견적 제출 핸들러
    const handleSubmitResponse = async () => {
        // 주문 제작 견적의 경우 상품 선택이 필요하지 않음
        const isCustomOrder = !quotation.quotationItems || quotation.quotationItems.length === 0;
        
        if (!isCustomOrder && selectedItems.length === 0) {
            alert('선택된 상품이 없습니다.');
            return;
        }

        if (!adminMemo.trim()) {
            alert('답변 내용을 입력해주세요.');
            return;
        }

        let finalAdminMemo = adminMemo;
        
        // 첨부파일이 있는 경우 메모에 추가
        if (uploadedFileUrl && fileName !== '선택된 파일 없음') {
            finalAdminMemo += `\n\n[첨부파일: ${fileName}] 파일 다운로드: ${uploadedFileUrl}`;
        }

        try {
            await updateQuotationStatus({
                variables: {
                    id: parseInt(quoteId),
                    status: 'COMPLETED',
                    adminReply: adminMemo,
                    attachmentUrl: uploadedFileUrl || null
                }
            });
        } catch (error) {
            console.error('견적 답변 에러:', error);
        }
    };

    // 고객 메시지에서 파일 정보 추출
    const parseCustomerFiles = (message) => {
        if (!message) return { text: '', files: [] };
        
        // 파일 패턴 매칭: === 첨부 파일 === 섹션 찾기
        const fileSection = message.match(/=== 첨부 파일 ===([\s\S]*?)(?=\n\n===|\n\n추가|$)/);
        if (!fileSection) return { text: message, files: [] };
        
        const fileSectionText = fileSection[1];
        const files = [];
        
        // 파일 라인 패턴: "숫자. 파일명 (크기)" 다음에 "다운로드: URL"
        const filePattern = /(\d+)\.\s*([^\n]+?)\s*\(([^)]+)\)\s*\n\s*다운로드:\s*(https?:\/\/[^\s]+)/g;
        let match;
        
        while ((match = filePattern.exec(fileSectionText)) !== null) {
            files.push({
                index: match[1],
                fileName: match[2].trim(),
                size: match[3].trim(),
                url: match[4].trim()
            });
        }
        
        // 파일 정보 제거한 텍스트
        const cleanText = message.replace(/=== 첨부 파일 ===[\s\S]*?(?=\n\n===|\n\n추가|$)/, '').trim();
        
        return { text: cleanText, files };
    };

    // 파일 다운로드 핸들러
    const handleCustomerFileDownload = (url, fileName) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || 'download';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // 파일 타입 확인 함수들
    const isImageFile = (fileName) => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp'];
        return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
    };

    const isPdfFile = (fileName) => {
        return fileName.toLowerCase().endsWith('.pdf');
    };

    // 유틸리티 함수들
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const getProductImage = (product) => {
        if (!product.images) return 'https://via.placeholder.com/100x60';
        
        try {
            const images = typeof product.images === 'string' ? JSON.parse(product.images) : product.images;
            return images[0] || 'https://via.placeholder.com/100x60';
        } catch (e) {
            return 'https://via.placeholder.com/100x60';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'PENDING': return '견적 미완료';
            case 'PROCESSING': return '견적 처리중';
            case 'COMPLETED': return '견적 완료';
            case 'REJECTED': return '견적 거절';
            default: return status;
        }
    };

    const getUserLevel = (level) => {
        switch (level) {
            case 'A_GRADE': return 'A급';
            case 'B_GRADE': return 'B급';
            case 'C_GRADE': return 'C급';
            case 'D_GRADE': return 'D급';
            default: return level;
        }
    };

    // 추가요청사항 파싱 함수
    const parseAdditionalRequest = (message) => {
        if (!message) return null;
        
        // 실제 데이터 형식에 맞는 패턴: "=== 추가 요청사항 ===\n내용..."
        const additionalRequestMatch = message.match(/=== 추가 요청사항 ===\s*\n([\s\S]*?)$/);
        if (additionalRequestMatch && additionalRequestMatch[1]) {
            const content = additionalRequestMatch[1].trim();
            return content || null;
        }
        return null;
    };

    // 로딩 및 에러 상태 처리
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <div>견적 상세 정보를 불러오는 중입니다...</div>
            </div>
        );
    }

    if (error || !data?.quotation) {
        return (
            <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
                <div>견적 정보를 불러오는 중 오류가 발생했습니다.</div>
                <button onClick={() => navigate('/admin/quotes')} style={{ marginTop: '20px', padding: '10px 20px' }}>
                    견적 목록으로 돌아가기
                </button>
            </div>
        );
    }

    const quotation = data.quotation;

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 견적 관리 &gt; 견적 답변하기</span>
            </div>
            <div className="admin-main-content">
                <main className="admin-content-response">
                    <div className="page-header">
                        <div className="page-title-bar"></div>
                        <h2>견적 답변하기</h2>
                    </div>

                    <table className="customer-info-table">
                        <tbody>
                            <tr>
                                <th>아이디</th><td>{quotation.user.id}</td>
                            </tr>
                            <tr>
                                <th>회사명</th><td>{quotation.user.company || '-'}</td>
                            </tr>
                            <tr>
                                <th>등급</th><td>{getUserLevel(quotation.user.level)}</td>
                            </tr>
                            <tr>
                                <th>성함</th><td>{quotation.user.name}</td>
                            </tr>
                            <tr>
                                <th>이메일</th><td>{quotation.user.email}</td>
                            </tr>
                            <tr>
                                <th>연락처</th><td>{quotation.user.phone || '-'}</td>
                            </tr>
                            <tr>
                                <th>팩스</th><td>{quotation.user.fax || '해당없음'}</td>
                            </tr>
                            <tr>
                                <th>우편번호</th><td>{quotation.user.zipCode || '-'}</td>
                            </tr>
                            <tr>
                                <th>기본 주소</th><td>{quotation.user.address || '-'}</td>
                            </tr>
                            <tr>
                                <th>상세 주소</th><td>{quotation.user.addressDetail || '-'}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* 등록된 배송지 정보 */}
                    {quotation.user.deliveryAddresses && quotation.user.deliveryAddresses.length > 0 && (
                        <div className="delivery-addresses-section">
                            <h4>등록된 배송지 정보</h4>
                            <div className="delivery-addresses-list">
                                {quotation.user.deliveryAddresses.map((address, index) => (
                                    <div key={address.id} className="delivery-address-card">
                                        <div className="address-header">
                                            <span className="address-name">
                                                {address.name || `배송지 ${index + 1}`}
                                            </span>
                                            {address.isDefault && (
                                                <span className="default-badge">기본 배송지</span>
                                            )}
                                        </div>
                                        <div className="address-details">
                                            <p><strong>수령인:</strong> {address.recipient}</p>
                                            <p><strong>연락처:</strong> {address.phone}</p>
                                            <p><strong>우편번호:</strong> {address.zipCode}</p>
                                            <p><strong>주소:</strong> {address.address}</p>
                                            {address.addressDetail && (
                                                <p><strong>상세주소:</strong> {address.addressDetail}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                     <table className="quote-details-table">
                        <tbody>
                            <tr>
                                <th>견적 번호</th><td>QT-{quotation.id.toString().padStart(6, '0')}</td>
                            </tr>
                            <tr>
                                <th>견적 일자</th><td>{formatDate(quotation.createdAt)}</td>
                            </tr>
                            <tr>
                                <th>견적 상태</th><td>{getStatusText(quotation.status)}</td>
                            </tr>
                             <tr>
                                <th>견적 담당자 (연락처)</th><td>010-3040-1630</td>
                            </tr>
                             <tr>
                                <th>이메일 발송</th><td>info@busbarworld.com</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="quote-items-list">
                        {quotation.quotationItems && quotation.quotationItems.length > 0 ? (
                            // 일반 상품 견적의 경우
                            quotation.quotationItems.map(item => (
                            <div key={item.id} className="quote-item-card">
                                <div className="card-header">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedItems.includes(item.id)}
                                        onChange={(e) => handleItemCheck(item.id, e.target.checked)}
                                    />
                                    <span>견적번호 QT-{quotation.id.toString().padStart(6, '0')}</span>
                                    <div className={`status-badge ${quotation.status === 'PENDING' ? 'pending' : ''}`}>
                                        {getStatusText(quotation.status)}
                                        <span>{formatDate(quotation.updatedAt)} 업데이트</span>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <ImageWithPlaceholder 
                                        src={getProductImage(item.product)} 
                                        alt={item.product.name} 
                                    />
                                    <div className="item-details">
                                        <p>{item.product.name}</p>
                                        <p>수량 : {item.quantity}개</p>
                                        <p>제품코드 : {item.product.id}</p>
                                        {item.product.description && (
                                            <p>설명 : {item.product.description}</p>
                                        )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            // 주문 제작 견적의 경우
                            <div className="custom-order-section">
                                <div className="custom-order-header">
                                    <h3>주문 제작 견적 요청</h3>
                                    <p>고객 맞춤형 제작 견적입니다. 아래 상세 내용과 첨부 파일을 확인해주세요.</p>
                                </div>
                                <div className="custom-order-info">
                                    <div className="info-item">
                                        <span className="label">견적 번호</span>
                                        <span className="value">QT-{quotation.id.toString().padStart(6, '0')}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">견적 유형</span>
                                        <span className="value">주문 제작</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">처리 상태</span>
                                        <span className="value">{getStatusText(quotation.status)}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {quotation.message && (
                        <div className="shipping-note">
                            <p>고객 요청사항</p>
                            <div className="customer-message">
                                <p style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{quotation.message}</p>
                            </div>
                        </div>
                    )}

                    {/* 주문 제작일 경우에만 추가요청사항 표시 */}
                    {(!quotation.quotationItems || quotation.quotationItems.length === 0) && parseAdditionalRequest(quotation.message) && (
                        <div className="additional-request-section">
                            <h3>추가 요청사항</h3>
                            <div className="additional-request-content">
                                <p style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                    {parseAdditionalRequest(quotation.message)}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* 새로운 customerFiles 필드를 사용한 첨부파일 표시 */}
                    {quotation.customerFiles && quotation.customerFiles.length > 0 && (
                        <div className="customer-files-section">
                            <h3>고객 첨부파일</h3>
                            <div className="customer-files">
                                {quotation.customerFiles.map((file, index) => (
                                    <div key={index} className="customer-file-item">
                                        <span className="file-info">
                                            📎 {file.name} ({(file.size / 1024).toFixed(1)}KB)
                                        </span>
                                        <div className="file-actions">
                                            <button 
                                                className="btn-download-small"
                                                onClick={() => handleCustomerFileDownload(file.url, file.name)}
                                            >
                                                다운로드
                                            </button>
                                            {isImageFile(file.name) || isPdfFile(file.name) ? (
                                                <button 
                                                    className="btn-preview-small"
                                                    onClick={() => window.open(file.url, '_blank')}
                                                >
                                                    미리보기
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="response-attachment-section">
                        <h3 className="section-title">답변 첨부하기</h3>
                        
                        {/* 기존 첨부파일이 있는 경우 표시 */}
                        {quotation.attachmentUrl && (
                            <div className="existing-attachment">
                                <h4>기존 첨부파일</h4>
                                <div className="existing-file-item">
                                    <span className="file-info">
                                        📎 기존 견적서 파일
                                    </span>
                                    <div className="file-actions">
                                        <button 
                                            className="btn-download-small"
                                            onClick={() => window.open(quotation.attachmentUrl, '_blank')}
                                        >
                                            다운로드
                                        </button>
                                        <button 
                                            className="btn-preview-small"
                                            onClick={() => window.open(quotation.attachmentUrl, '_blank')}
                                        >
                                            미리보기
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div className="attachment-upload-section">
                            <h4>{quotation.attachmentUrl ? '새 파일 업로드 (기존 파일 교체)' : '견적서 첨부하기'}</h4>
                            <div className="attachment-row">
                                <input 
                                    type="file" 
                                    id="attachmentFile"
                                    onChange={handleFileChange}
                                    accept=".pdf,.xlsx,.xls,.doc,.docx"
                                    style={{ display: 'none' }}
                                />
                                <button onClick={() => document.getElementById('attachmentFile').click()} disabled={isUploading}>
                                    {isUploading ? '업로드 중...' : '파일 선택'}
                                </button>
                                <span>{fileName}</span>
                                <input 
                                    type="checkbox" 
                                    id="includeVat" 
                                    checked={includeVat}
                                    onChange={(e) => setIncludeVat(e.target.checked)}
                                />
                                <label htmlFor="includeVat">부가세 포함</label>
                            </div>
                        </div>
                    </div>

                    <div className="admin-memo-section">
                        <h3 className="section-title">관리자 메모</h3>
                        {quotation.adminReply && (
                            <div className="existing-reply">
                                <h4>기존 답변</h4>
                                <div className="existing-reply-content">
                                    <p style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                        {quotation.adminReply}
                                    </p>
                                </div>
                            </div>
                        )}
                        <h4>{quotation.adminReply ? '답변 수정' : '새 답변 작성'}</h4>
                        <textarea 
                            placeholder="납기 준수 필요 / 양산건 추가 발주 가능 여부 파악"
                            value={adminMemo}
                            onChange={(e) => setAdminMemo(e.target.value)}
                        />
                    </div>

                    <div className="form-actions">
                        <button className="btn-secondary" onClick={() => navigate(-1)}>
                            <span className="btn-icon">&lt;</span> 돌아가기
                        </button>
                        <button 
                            className="btn-primary" 
                            onClick={handleSubmitResponse}
                            disabled={updating}
                        >
                            {updating ? '처리 중...' : '답변 완료'} <span className="btn-icon">&gt;</span>
                        </button>
                    </div>

                </main>
            </div>
        </>
    );
};

export default QuotationResponsePage; 